// Picks the menu roles (RolesNew rows) of one company and factory for the chosen language.
//
// RolesNew rows carry a LanguageKey, but on staging and production every row is "en": the
// menu titles are translated in the browser through the locale files (the English title is
// the key). Keeping only the rows whose languageKey equals the chosen language left the menu
// and every screen empty in Spanish, Portuguese, French and Thai (QA UI-01).
//
// Rule: the rows in the chosen language when the factory has any, otherwise the English
// rows, otherwise whatever rows the factory has.

export const FALLBACK_ROLE_LANGUAGE = "en";

// Ids may come as numbers or strings, so they are compared as text.
const same = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
const ofFactory = (roles, companyId, factoryId) =>
  (roles || []).filter((x) => same(x.companyId, companyId) && same(x.factoryId, factoryId));

/** The languageKey whose rows are used for this company and factory. */
export const roleLanguageFor = (roles, companyId, factoryId, lang) => {
  const rows = ofFactory(roles, companyId, factoryId);
  if (lang && rows.some((x) => x.languageKey === lang)) return lang;
  if (rows.some((x) => x.languageKey === FALLBACK_ROLE_LANGUAGE)) return FALLBACK_ROLE_LANGUAGE;
  return rows.length ? rows[0].languageKey : lang || FALLBACK_ROLE_LANGUAGE;
};

/** Every row of the company and factory in the language used for the menu (duplicates kept). */
export const rolesInLanguage = (roles, companyId, factoryId, lang) => {
  const useLang = roleLanguageFor(roles, companyId, factoryId, lang);
  return ofFactory(roles, companyId, factoryId).filter((x) => x.languageKey === useLang);
};

// Two screens share the misspelt title "Perventive Maintenance Report" in RolesNew, so the menu
// and its search could not tell the compliance report apart (QA UI-09). Until the rows are
// corrected, the compliance report is shown under its own title key. Misspelt titles in general
// are mapped to the right words by locale keys.
const TITLE_BY_PATH = {
  PreventiveMaintenancePlanComplianceReport: { from: "Perventive Maintenance Report", to: "PMComplianceReport" },
};
const displayRole = (x) => {
  const fix = TITLE_BY_PATH[x.path];
  return fix && normalizeTitle(x.title) === fix.from ? { ...x, title: fix.to } : x;
};

/** The rows the menu and the routes are built from. Returns a new array (callers push to it). */
export const selectFactoryRoles = (roles, companyId, factoryId, lang) =>
  rolesInLanguage(roles, companyId, factoryId, lang).map(displayRole);

/** A role title as a translation key: spaces and tabs tidied (some rows have stray ones). */
export const normalizeTitle = (title) =>
  typeof title === "string" ? title.replace(/\s+/g, " ").trim() : title;
